import { TreeData } from "@/utils/tree-generation/tree-data";
import { GUID } from "@faro-lotv/foundation";

/**
 * @param nodes The tree nodes to collect the scans from
 * @param invalidIds Clusters without scans that can be ordered
 * @returns the ids of the scans below the nodes
 */
export function collectScanIds(
  nodes: TreeData[],
  invalidIds: Set<GUID>,
): GUID[] {
  return nodes.flatMap((node) => {
    if (invalidIds.has(node.id)) return [];
    return node.children
      ? collectScanIds(node.children, invalidIds)
      : [node.id];
  });
}

/**
 * Selection of the scans sent to IPX, like the trees of the Viewer: a click selects only the clicked item,
 * a toggle (Shift/Ctrl/Cmd+click or long press) adds it to or removes it from the selection.
 * The selection is never empty: an empty list means the whole point cloud.
 * Adding to the whole point cloud starts a new selection with the item, instead of removing it from the rest.
 *
 * @param selectedIds The selected scans, empty for the whole point cloud
 * @param allScanIds All the scans of the point cloud
 * @param clickedScanIds The scans of the clicked item: a scan, a cluster or the point cloud
 * @param isToggle True to add or remove the item instead of selecting only it
 * @returns the new selected scans, empty for the whole point cloud
 */
export function nextIpxScanSelection(
  selectedIds: GUID[],
  allScanIds: GUID[],
  clickedScanIds: GUID[],
  isToggle: boolean,
): GUID[] {
  const next = new Set(isToggle ? selectedIds : []);
  const isRemoving = isToggle && clickedScanIds.every((id) => next.has(id));
  for (const id of clickedScanIds) {
    if (isRemoving) next.delete(id);
    else next.add(id);
  }
  // The last selected item can't be removed, as there would be nothing to send
  if (!next.size) return selectedIds;
  return next.size === allScanIds.length
    ? []
    : allScanIds.filter((id) => next.has(id));
}

/**
 * @param rows The visible rows of the tree, in their order
 * @param anchorId The row clicked before, where the range starts
 * @param targetId The row Shift+clicked, where the range ends
 * @param invalidIds Clusters without scans that can be ordered
 * @returns the scans of all the rows between the two, undefined if one of them is not visible
 */
export function collectRangeScanIds(
  rows: TreeData[],
  anchorId: GUID,
  targetId: GUID,
  invalidIds: Set<GUID>,
): GUID[] | undefined {
  const anchorIndex = rows.findIndex((row) => row.id === anchorId);
  const targetIndex = rows.findIndex((row) => row.id === targetId);
  if (anchorIndex < 0 || targetIndex < 0) return;
  const range = rows.slice(
    Math.min(anchorIndex, targetIndex),
    Math.max(anchorIndex, targetIndex) + 1,
  );
  // An expanded cluster and its scans are both in the range
  return [...new Set(collectScanIds(range, invalidIds))];
}

/**
 * Checkbox selection, on touch screens: every scan is checked while the whole point cloud is selected,
 * so checking or unchecking an item adds it to or removes it from what is checked
 *
 * @param selectedIds The selected scans, empty for the whole point cloud
 * @param allScanIds All the scans of the point cloud
 * @param clickedScanIds The scans of the tapped item: a scan, a cluster or the point cloud
 * @returns the new selected scans, empty for the whole point cloud
 */
export function toggleIpxScanCheckbox(
  selectedIds: GUID[],
  allScanIds: GUID[],
  clickedScanIds: GUID[],
): GUID[] {
  return nextIpxScanSelection(
    selectedIds.length ? selectedIds : allScanIds,
    allScanIds,
    clickedScanIds,
    true,
  );
}
